import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Carousel, Dropdown } from 'react-bootstrap';
import { isWithinInterval, parseISO } from 'date-fns';

import { UseOutletContextPosSale } from './PosSale';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { InventoryReference } from '../../../../models/inventory.reference.model';
import { Names } from '../../../../models/names.model';
import { Service } from '../../../../models/service.model';
import { Tax } from '../../../../models/tax.model';
import { PosCashier } from '../../../../models/pos.model';
import { Variant } from '../../../../models/variant.model';
import { ApiDigitalAdminContact } from '../../../../services/admin/contact.service';
import { PosCashierDA } from '../../../../services/admin/pos.cashier.service';
import { PosSaleDA } from '../../../../services/admin/pos.sale.service';
import { ServiceDA } from '../../../../services/admin/service.service';
import { ProductDA } from '../../../../services/admin/product.service';
import { AccountingEntryDA } from '../../../../services/admin/accounting.entry.service';
import { AccountingEntry } from '../../../../models/accounting.entry.model';
import { Error } from '../../../../models/error.model';
import { Container } from '../../../../styles/container.style';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Message } from '../../../../models/message.model';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { calculatePriceUnit, calculateRebate } from '../../../../scripts/financial.calculation.script';
import { formatContentDetail, formatMoney, formatTaxRate } from '../../../../scripts/format.value.script';
import { contactToCustomer } from '../../../../scripts/customer.mapper.script';
import { SALE_ERROR_MESSAGES } from '../../../../scripts/sale.error.messages.script';
import { legendInvalid, legendInvalidRequired, legendValid, legendValidInvalidRestart, legendValidRequired, legendValidRequiredRestart } from '../../../../utils/legend.data.util';
import { imageProduct, imageService } from '../../../../scripts/image.value.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { evaluateLegendListObjectOptional, evaluateLegendListObjectRequired } from '../../../../scripts/validate.legend.script';
import { taxSaleCodes } from '../../../../libraries/tax.code.library';
import { isValidObjectAttributes } from '../../../../scripts/compare.object.script';
import { getError, getMessage } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { inventoryTypeValue } from '../../../../libraries/inventory.type.library';

import AppInputContact from '../../../../components/element/InputContact';
import AppPreloader from '../../../../components/Preloader';
import AppLegend from '../../../../components/element/Legend';
import PosPaymentForm from './component/PosPaymentForm';
import imgServiceDefault from '../../../../assets/themes/service.png';
import imgProductDefault from '../../../../assets/themes/product.png';

export interface POSCreatePageProps {};

let errorResponse: Error, messageResponse: Message, posCashierResponse: PosCashier;

const POSCreatePage: React.FunctionComponent<POSCreatePageProps> = props => {
  const {setRoute, companyForUser, appServiceForUser, contactsForCompany, servicesForCompany, productVariantsForCompany, treasuryAccountsForCompany, loadContactsForCompany, settingsForCompany, accountingCreditEntriesForCompany} = UseOutletContextPosSale()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const param = useParams()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [step, setStep] = useState(1)
  const [customer, setCustomer] = useState({value: '', valid: false, object: null as any})
  const [requirements, setRequirements] = useState({list: true, value: [{reference: '', type: '', description: '', unit: null as any, price_unit: '', quantity: '', tax_code: '', tax_rate: 0, rebate_type: '', rebate_value: 0, rebate_quantity: 0, rebate_amount: '', stock: 0, photos: [] as string[], product_account_id: null as string | null}], valid: [{reference: false, quantity: false, tax: false, rebate: false}]})
  const [search, setSearch] = useState<{text: string, list: InventoryReference[]}>({text: '', list: []})
  const [list, setList] = useState<InventoryReference[]>([])
  const [posCashier, setPosCashier] = useState<PosCashier | null>(null)
  const [localServices, setLocalServices] = useState<Service[] | null>(null)
  const [localProductVariants, setLocalProductVariants] = useState<Variant[] | null>(null)
  const [localContacts, setLocalContacts] = useState<any[]>([])
  const [localAccountingCreditEntries, setLocalAccountingCreditEntries] = useState<AccountingEntry[]>([])
  const [paymentFormState, setPaymentFormState] = useState<{payments: {list: boolean, value: {method: string, id_accounting_entry: any, amount: string, received: string, change: string, currency_code: string, amount_chf: string, exchange_rate: string, exchange_date: string, id_treasury_account: string, change_amount: string, change_currency_code: string}[], valid: {id_accounting_entry: boolean, amount: boolean, received: boolean}[]}, isValid: boolean}>({payments: {list: false, value: [], valid: []}, isValid: false})

  const handlePaymentsChange = useCallback((data: {payments: {list: boolean, value: {method: string, id_accounting_entry: any, amount: string, received: string, change: string, currency_code: string, amount_chf: string, exchange_rate: string, exchange_date: string, id_treasury_account: string, change_amount: string, change_currency_code: string}[], valid: {id_accounting_entry: boolean, amount: boolean, received: boolean}[]}, isValid: boolean}) => {
    setPaymentFormState(data)
  }, [])

  const loadPosCashier = async (id_company: string, id_pos_cashier: string) => {
    await PosCashierDA.getCashier(id_company, id_pos_cashier).then( (response) => {
      if (response.status === 200) {
        posCashierResponse = response.data

        setPosCashier(posCashierResponse)
        const customerRef = (posCashierResponse as any).default_customer ?? posCashierResponse.customer_default ?? (posCashierResponse as any).id_customer
        const customerId = typeof customerRef === 'string' ? customerRef : customerRef?.id ?? ''
        const customerObj = typeof customerRef === 'object' ? customerRef : null
        setCustomer({value: customerId, valid: !!customerId, object: customerObj})
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }


  const submitPosSaleCreate = async () => {
    setLoadIndicator('on')

    let requerimentsIsValid: boolean = requirements.valid.every((item) => isValidObjectAttributes(item))

    if (companyForUser && posCashier && customer.valid && paymentFormState.isValid && requerimentsIsValid) {
      let requirementList = requirements.value.map((item) => ({reference: item.reference, type: item.type, description: item.description, unit: item.unit, quantity: item.quantity, price_unit: item.price_unit, tax_code: item.tax_code, tax_rate: item.tax_rate, rebate_type: item.rebate_type, rebate_value: item.rebate_value, rebate_quantity: item.rebate_quantity, rebate_amount: item.rebate_amount, product_account_id: item.product_account_id || null}))
      let paymentList = paymentFormState.payments.value.map((item) => ({method: item.method, id_accounting_entry: item.id_accounting_entry ? item.id_accounting_entry.id : '', currency_code: item.currency_code, amount: item.amount, exchange_rate: item.exchange_rate, exchange_date: item.exchange_date, id_treasury_account: item.id_treasury_account, received: item.received, change_amount: item.change_amount, change_currency_code: item.change_currency_code}))

      const contacts = localContacts.length > 0 ? localContacts : (contactsForCompany ?? [])
      const customerContact = customer.object && contacts.length > 0 ? contacts.find((c: any) => c.id === customer.value) : null
      const customerPayload = customerContact ? contactToCustomer(customerContact) : null

      // Compute local amount for mismatch detection (backend is source of truth)
      const localAmount = calculatorAmount()
      const expectedAmount = {
        sub_total: localAmount.sub_total,
        rebate_total: localAmount.rebate_total,
        tax_total: localAmount.tax_total,
        total: localAmount.total,
      }

      await PosSaleDA.postSale(companyForUser.id, posCashier.id, customerPayload, companyForUser.currency, requirementList, paymentList, expectedAmount, {
        uuid: companyForUser.uuid,
        name: companyForUser.name,
        logo: companyForUser.logo,
        address: companyForUser.address,
        phone: companyForUser.phone || '',
        email: companyForUser.email || '',
        website: companyForUser.website || '',
        vat_number: companyForUser.vat_number || '',
      }, {
        subject_tax: settingsForCompany?.subject_tax ?? true,
        tax_method: settingsForCompany?.tax_method ?? 'effective',
        tax_declaration: settingsForCompany?.tax_declaration ?? 'invoicing',
      }, lang.code.toUpperCase()).then( (response) => {
        if (response.status === 201) {
          messageResponse = response.data

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate('/main/pos/view/' + param.id, {replace: true})
          })
        } else {
          errorResponse = response.data

          // Special handling for amount mismatch
          if (errorResponse.code === 'SALE_AMOUNT_MISMATCH') {
            console.error('[POS] Amount mismatch detected:', {
              localCalculation: localAmount,
              backendMessage: errorResponse.message,
              requirements: requirements.value,
            })
          }

          const userMessage = SALE_ERROR_MESSAGES[errorResponse.code] || getError(errorResponse.code, lang.code, errorResponse.message)

          GlobalSwal.fire({
            title: userMessage,
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeDescriptionRequirement = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let text: string = event.target.value
    let tempList: InventoryReference[] = []

    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.description = text
      }
      return temp_item
    })

    if (text.length > 0) {
      for (let reference of list.flatMap(item => item)) {
        if (reference.display_name.toLowerCase().indexOf(text.toLowerCase()) !== -1 || reference.description.toLowerCase().indexOf(text.toLowerCase()) !== -1 || reference.code.toLowerCase().indexOf(text.toLowerCase()) !== -1) {
          tempList.push(reference)
        }
      }
    } else {
      tempList = list
    }

    setRequirements({...requirements, value: tempRequirementsValue})
    setSearch({text: text, list: tempList})
  }

  const handleChangeQuantityRequirement = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let val: string = event.target.value

    if (event.target.value.length > 0 && !isNaN(event.target.value)) {
      let qty: number = parseInt(event.target.value, 10)
      val = (posCashier?.stock_enabled !== false && qty > requirements.value[index].stock) ? String(requirements.value[index].stock) : String(qty)
    }

    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.quantity = val
        temp_item.rebate_amount = calculateRebate(temp_item.reference, temp_item.quantity, temp_item.price_unit, false, temp_item.tax_rate, temp_item.rebate_type, temp_item.rebate_value, temp_item.rebate_quantity, temp_item.rebate_amount)
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue})
  }

  const handleChangeTaxRateRequirement = (index: number, item: Tax) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.tax_code = item.code
        temp_item.tax_rate = item.rate
        temp_item.rebate_amount = calculateRebate(temp_item.reference, temp_item.quantity, temp_item.price_unit, false, item.rate, temp_item.rebate_type, temp_item.rebate_value, temp_item.rebate_quantity, temp_item.rebate_amount)
      }
      return temp_item
    })
    let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.tax = true
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})
    legendValid(`legend-requirement-tax-required${suffixLegend(index)}`)
    validateRebaja(index)
  }

  const handleChangeRebateRequirement = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.rebate_amount = event.target.value
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue})
  }

  const validateQuantityRequirement = (index: number) => {
    if (parseInt(requirements.value[index].quantity, 10) === 0) {
      legendInvalid(`legend-requirement-quantity-valid${suffixLegend(index)}`)
      legendValid(`legend-requirement-quantity-required${suffixLegend(index)}`)
    } else {
      evaluateLegendListObjectRequired(expressions.quantity, index, 'quantity', requirements, setRequirements, `legend-requirement-quantity-valid${suffixLegend(index)}`, `legend-requirement-quantity-required${suffixLegend(index)}`)
    }
    validateRebaja(index)
  }

  const validateRebateRequirement = (index: number) => {
    evaluateLegendListObjectOptional(expressions.currency, index, 'rebate_amount', requirements, setRequirements, `legend-requirement-rebate-valid${suffixLegend(index)}`)
  }

  const executeAddRequirement = () => {
    if (allowRequirement()) {
      let newRequirimentValue = {reference: '', type: '', description: '', unit: null, price_unit: '', quantity: '', tax_code: '', tax_rate: 0, rebate_type: '', rebate_value: 0, rebate_quantity: 0, rebate_amount: '', stock: 0, photos: [] as string[], product_account_id: null as string | null}
      let newRequirementValid = {reference: false, quantity: false, tax: false, rebate: false}

      setRequirements({list: true, value: [...requirements.value, newRequirimentValue], valid: [...requirements.valid, newRequirementValid]})
      uploadSearch()
    }

    legendValid('legend-requirement-required')
  }

  const executeRemoveRequirement = (index: number) => {
    let tempRequirementsValue = requirements.value.filter((_, temp_index) => (temp_index !== index))
    let tempRequirementsValid = requirements.valid.filter((_, temp_index) => (temp_index !== index))

    setRequirements({list: !(tempRequirementsValue.length === 0 && tempRequirementsValid.length === 0), value: tempRequirementsValue, valid: tempRequirementsValid})

    if (tempRequirementsValue.length === 0 && tempRequirementsValid.length === 0) {
      legendInvalid('legend-requirement-required')
    }

    restartFormRequirement(tempRequirementsValue)
  }

  const executeRemoveReferenceRequirement = (index: number) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.reference = ''
        temp_item.type = ''
        temp_item.description = ''
        temp_item.unit = null
        temp_item.price_unit = ''
        temp_item.quantity = ''
        temp_item.tax_code = ''
        temp_item.tax_rate = 0
        temp_item.rebate_type = ''
        temp_item.rebate_value = 0
        temp_item.rebate_quantity = 0
        temp_item.rebate_amount = ''
        temp_item.stock = 0
        temp_item.photos = []
        temp_item.product_account_id = null
      }
      return temp_item
    })

    let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.reference = false
        temp_item.quantity = false
        temp_item.tax = false
        temp_item.rebate = false
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})
    legendValidInvalidRestart(`legend-requirement-reference-required${suffixLegend(index)}`)
    legendValidRequiredRestart(`legend-requirement-quantity-valid${suffixLegend(index)}`, `legend-requirement-quantity-required${suffixLegend(index)}`)
    legendValidInvalidRestart(`legend-requirement-tax-required${suffixLegend(index)}`)
    legendValidRequiredRestart(`legend-requirement-rebate-valid${suffixLegend(index)}`, `legend-requirement-rebate-required${suffixLegend(index)}`)
  }

  const executeSelectQuantityRequirement = (index: number, item: 'plus' | 'dash') => {
    if (requirements.valid[index].reference) {
      let val: string = requirements.value[index].quantity

      if (val.trim().length > 0 && !isNaN(Number(val))) {
        let qty: number = parseInt(val, 10)
        qty = (item === 'plus') ? qty + 1 : qty - 1
        qty = (posCashier?.stock_enabled !== false && qty > requirements.value[index].stock) ? requirements.value[index].stock : qty
        qty = (qty > 0) ? qty : 0
        val = String(qty)
      } else {
        val = '0'
      }

      let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
        if (index === temp_index) {
          temp_item.quantity = val
          temp_item.rebate_amount = calculateRebate(temp_item.reference, temp_item.quantity, temp_item.price_unit, false, temp_item.tax_rate, temp_item.rebate_type, temp_item.rebate_value, temp_item.rebate_quantity, temp_item.rebate_amount)
        }
        return temp_item
      })
      let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
        if (index === temp_index) {
          temp_item.quantity = !(val === '0')
        }
        return temp_item
      })

      setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})
      validateQuantityRequirement(index)
    }
  }

  const executeSelectReferenceRequirement = (index: number, item: InventoryReference) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.reference = (item.type === 'service') ? item.id_service : item.id_variant
        temp_item.type = item.type
        temp_item.description = formatContentDetail(item)
        temp_item.unit = item.unit
        temp_item.price_unit = item.price_sale.amount.toString()
        temp_item.quantity = '1'
        temp_item.tax_code = item.price_sale.tax_code
        temp_item.tax_rate = item.price_sale.tax_rate
        temp_item.rebate_type = item.discount.type
        temp_item.rebate_value = item.discount.value
        temp_item.rebate_quantity = item.discount.quantity
        temp_item.rebate_amount = calculateRebate((item.type === 'service') ? item.id_service : item.id_variant, temp_item.quantity, item.price_sale.amount.toString(), false, item.price_sale.tax_rate, item.discount.type, item.discount.value, item.discount.quantity, '0')
        temp_item.stock = item.stock
        temp_item.photos = item.photos
        temp_item.product_account_id = item.product_account_id || null
      }
      return temp_item
    })
    let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.reference = true
        temp_item.quantity = true
        temp_item.tax = true
        temp_item.rebate = true
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})
    legendValid(`legend-requirement-reference-required${suffixLegend(index)}`)
    legendValidRequired(`legend-requirement-quantity-valid${suffixLegend(index)}`, `legend-requirement-quantity-required${suffixLegend(index)}`)
    legendValid(`legend-requirement-tax-required${suffixLegend(index)}`)
    legendValidRequired(`legend-requirement-rebate-valid${suffixLegend(index)}`, `legend-requirement-rebate-required${suffixLegend(index)}`)
  }

  const executeFormPrevious = () => {
    setStep(1)
  }

  const executeFormNext = () => {
    if (customer.value && requirements.valid.every((item) => isValidObjectAttributes(item))) {
      setStep(2)
    } else {
      if (!customer.value) legendInvalid('legend-contact-required')
      for (let i = 0; i < requirements.value.length; i++) {
        if (!requirements.value[i].description) legendInvalid(`legend-requirement-reference-required${suffixLegend(i)}`)
        if (!requirements.value[i].quantity) legendInvalid(`legend-requirement-quantity-required${suffixLegend(i)}`)
        if (!requirements.value[i].tax_rate) legendInvalid(`legend-requirement-tax-required${suffixLegend(i)}`)
        if (!requirements.value[i].rebate_amount) legendInvalid(`legend-requirement-rebate-required${suffixLegend(i)}`)
      }
    }
  }

  function restartFormRequirement(items: {reference: string, type: string, description: string, unit: any, price_unit: string, quantity: string, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, stock: number, photos: string[], product_account_id?: string | null}[]) {
    if (!expressions) return

    items.forEach((item, index) => {
      if (item.reference.length > 0) {
        legendValid(`legend-requirement-reference-required${suffixLegend(index)}`)
      } else {
        legendInvalid(`legend-requirement-reference-required${suffixLegend(index)}`)
      }
      if (item.tax_code.length > 0) {
        legendValid(`legend-requirement-tax-required${suffixLegend(index)}`)
      } else {
        legendInvalid(`legend-requirement-tax-required${suffixLegend(index)}`)
      }
      if (expressions.quantity.test(item.quantity)) {
        legendValidRequired(`legend-requirement-quantity-valid${suffixLegend(index)}`, `legend-requirement-quantity-required${suffixLegend(index)}`)
      } else {
        legendInvalidRequired(item.quantity, `legend-requirement-quantity-valid${suffixLegend(index)}`, `legend-requirement-quantity-required${suffixLegend(index)}`)
      }
      if (expressions.currency.test(item.rebate_amount)) {
        legendValidRequired(`legend-requirement-rebate-valid${suffixLegend(index)}`, `legend-requirement-rebate-required${suffixLegend(index)}`)
      } else {
        legendInvalidRequired(item.rebate_amount, `legend-requirement-rebate-valid${suffixLegend(index)}`, `legend-requirement-rebate-required${suffixLegend(index)}`)
      }
    })
  }

  function validateRebaja(index: number) {
    let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
      if (index === temp_index) {
        if (temp_item.reference) {
          temp_item.rebate = requirements.value[index].rebate_amount.length > 0 
        } else {
          temp_item.rebate = requirements.value[index].rebate_amount.length === 0 || expressions.currency.test(requirements.value[index].rebate_amount)
        }
      }
      return temp_item
    })

    if (tempRequirementsValid[index].rebate) {
      legendValid(`legend-requirement-rebate-valid${suffixLegend(index)}`)
    } else {
      legendValidInvalidRestart(`legend-requirement-rebate-valid${suffixLegend(index)}`)
    }
  }

  const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100

  function calculatorAmount(): {sub_total: number, rebate_total: number, after_rebate_total: number, tax_total: number, total: number, requirement: number[]} {
    let requirementPrices: number[] = []
    let subTotal: number = 0
    let rebateTotal: number = 0
    let afterRebateTotal: number = 0
    let taxTotal: number = 0
    let total: number = 0

    for (let item of requirements.value) {
      let quantity: number = Number(item.quantity)
      let price: number = calculatePriceUnit(item.price_unit, false, item.tax_rate)
      let rebate: number = Number(item.rebate_amount) / quantity
      let tax: number = (price - rebate) * item.tax_rate
      let requirement: number = quantity * (price + tax- rebate)

      subTotal += (quantity * price)
      rebateTotal += (quantity * rebate)
      afterRebateTotal += (quantity * (price - rebate))
      taxTotal += (quantity * tax)
      total += (quantity * (price + tax- rebate))

      requirementPrices.push(round2(requirement))
    }

    return {sub_total: round2(subTotal), tax_total: round2(taxTotal), rebate_total: round2(rebateTotal), after_rebate_total: round2(afterRebateTotal), total: round2(total), requirement: requirementPrices}
  }

  function allowRequirement(): boolean {
    let allow: boolean = true

    if (requirements.valid.length > 0) {
      for (let i in requirements.valid) {
        if (!requirements.valid[i].reference || !requirements.valid[i].quantity || !requirements.valid[i].tax || !requirements.valid[i].rebate) {
          if (!requirements.valid[i].reference) legendInvalid(`legend-requirement-reference-required${suffixLegend(i)}`)
          if (!requirements.valid[i].quantity) legendInvalidRequired(requirements.value[i].quantity, `legend-requirement-quantity-valid${suffixLegend(i)}`, `legend-requirement-quantity-required${suffixLegend(i)}`)
          if (!requirements.valid[i].tax) legendInvalid(`legend-requirement-tax-required${suffixLegend(i)}`)
          if (!requirements.valid[i].rebate) legendInvalidRequired(requirements.value[i].rebate_amount, `legend-requirement-rebate-valid${suffixLegend(i)}`, `legend-requirement-rebate-required${suffixLegend(i)}`)
          allow = false
        }
      }
    }

    return allow
  }

  function uploadSearch() {
    setSearch({text: '', list: list})
  }

  useEffect( () => {
    let tempServices: InventoryReference[] = (localServices) ? localServices.flatMap((item: Service) => { return {
      type: 'service',
      id_service: item.id,
      id_product: '',
      id_variant: '',
      display_name: item.display_name,
      description: item.description,
      code: item.code,
      price_sale: item.price_sale,
      price_purchase: item.price_purchase,
      unit: item.unit,
      discount: item.discount,
      stock: 999,
      photos: item.photos,
      product_account_id: item.product_account_id || item.accounts?.income || null
    }}) : []

    const stockFilter = posCashier?.stock_enabled === false ? ((_item: Variant) => true) : ((item: Variant) => item.stock > 0)
    let tempProducts: InventoryReference[] = (localProductVariants) ? localProductVariants.filter(stockFilter).flatMap((item: Variant) => { return {
      type: 'product',
      id_service: '',
      id_product: item.id_product,
      id_variant: item.id,
      display_name: item.display_name,
      description: item.description_short,
      code: item.code,
      price_sale: item.price_sale,
      price_purchase: item.price_purchase,
      unit: item.unit,
      discount: item.discount,
      stock: item.stock,
      photos: item.photos,
      product_account_id: item.product_account_id || null
    }}) : []

    setList([...tempServices, ...tempProducts])
    setSearch({text: '', list: [...tempServices, ...tempProducts]})

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localServices, localProductVariants, posCashier])

  useEffect( () => {
    if (companyForUser && param.id) {
      loadPosCashier(companyForUser.id, param.id)

      ServiceDA.getServices(companyForUser.id, '', '', 'display_name', 'asc').then((response) => {
        if (response.status === 200) {
          setLocalServices(response.data?.services ?? response.data?.items ?? [])
        }
      }).catch(() => {})

      ProductDA.getProductVariants(companyForUser.id, '', '', 'display_name', 'asc').then((response) => {
        if (response.status === 200) {
          setLocalProductVariants(response.data?.variants ?? response.data?.items ?? [])
        }
      }).catch(() => {})

      AccountingEntryDA.getAccountingCreditIncompleteEntries(companyForUser.id).then((response) => {
        if (response.status === 200) {
          setLocalAccountingCreditEntries(response.data?.accounting_entries ?? response.data?.items ?? [])
        }
      }).catch(() => {})

      ApiDigitalAdminContact.getContacts(companyForUser.id, '', '', '', '', 1, 9999).then((response) => {
        if (response.status === 200) {
          const data = response.data?.data ?? response.data
          setLocalContacts(data.items ?? data.contacts ?? data ?? [])
        }
      }).catch(() => {})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect(() => {
    setMounted(true)
    setRoute({ path: {root: lang.labels.pos, branch: lang.labels.createSale}, company: false })

    return () => setMounted(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && posCashier
        ?
        <div className="form">
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <h3>{lang.labels.createNewSale}</h3>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-content-center">
                  <span className={`badge badge-lg ${step === 1 ? "badge-info" : "badge-light-info"}`}>
                    {lang.labels.step1.toUpperCase()}: {lang.labels.selectProducts}
                  </span>
                  <span className={`badge badge-lg ms-2 ${step === 2 ? "badge-info" : "badge-light-info"}`}>
                    {lang.labels.step2.toUpperCase()}: {lang.labels.payments}
                  </span>
                </div>
              </div>
            </div>
            <div className="card-body">
              <Container property={(step === 1).toString()}>
                <div className="row mb-0 mb-lg-2">
                  <label className="col-lg-2 col-form-label required">{lang.labels.customer}</label>
                  <div className="col-lg-10">
                    <AppInputContact appServiceForUser={appServiceForUser} companyForUser={companyForUser} contact={customer} setContact={setCustomer} loadContactsForCompany={loadContactsForCompany}></AppInputContact>
                  </div>
                </div>
                <div className="separator separator-dashed my-5"></div>
                <div className="dataTables_wrapper">
                  <div className="table-responsive">
                    <table className="table align-middle g-1 mb-2">
                      <thead className="border-bottom">
                        <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                          <th className="min-w-250px text-nowrap">{lang.labels.description}</th>
                          <th className="w-100px text-center text-nowrap">{lang.labels.unitMeasure}</th>
                          <th className="w-150px text-center text-nowrap">{lang.labels.quantity}</th>
                          <th className="w-150px text-center text-nowrap">{lang.labels.unitPrice} ({companyForUser.currency?.code})</th>
                          <th className="w-150px text-center text-nowrap">{lang.labels.taxRate} (%)</th>
                          <th className="w-150px text-center text-nowrap">{lang.labels.rebate} ({companyForUser.currency?.code})</th>
                          <th className="w-50px"></th>
                        </tr>
                      </thead>
                      <tbody>
                        { requirements.list
                          ?
                          <>
                            { requirements.value.map (( (item, index) => { return (
                              <tr key={index}>
                                <td valign="top">
                                  <Dropdown>
                                    <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                                      <span className="selection bg-light rounded d-flex align-items-center">
                                        { requirements.valid[index].reference
                                          ?
                                          <>
                                            { item.photos.length > 0
                                              ?
                                              <Carousel slide={false} controls={false} indicators={false} touch={false}>
                                                { item.photos.map (( (sub_item, sub_index) => { return (
                                                  <Carousel.Item key={sub_index}>
                                                    <img src={item.type === "product" ? imageProduct(sub_item) : imageService(sub_item)} className="d-block rounded w-40px h-40px" alt={lang.labels.photography + index} />
                                                  </Carousel.Item>
                                                )}))}
                                              </Carousel>
                                              :
                                              <span className="symbol symbol-40px">
                                                <span className="symbol-label">
                                                  <img src={item.type === "product" ? imgProductDefault : imgServiceDefault} alt={lang.labels.photography} className="w-100 h-100" />
                                                </span>
                                              </span>
                                            }
                                            <span className="form-control text-start px-2">{item.description}</span>
                                          </>
                                          :
                                          <input className="form-control rounded-0 px-2" type="text" autoComplete="off" name="description" placeholder={lang.labels.search} value={item.description} onChange={(event) => handleChangeDescriptionRequirement(index, event)} />
                                        }
                                        <span className={`btn btn-light-primary btn-icon ${!requirements.valid[index].reference && "d-none"}`} onClick={() => executeRemoveReferenceRequirement(index)}>
                                          <i className="bi bi-x fs-1 fw-bolder mx-1" />
                                        </span>
                                      </span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                                      <span className="select2-dropdown select2-dropdown--below">
                                        <span className="select2-results">
                                          <ul className="select2-results__options">
                                            { search.list.length > 0
                                              ?
                                              <>
                                                { search.list.map (( (other_item, other_index) => { return (
                                                  <li key={other_index} className={`select2-results__option select2-results__option--selectable ${(other_item.type === "service" ? other_item.id_service : other_item.id_variant) === item.reference && "select2-results__option--selected"}`} role="option" aria-selected={(other_item.type === "service" ? other_item.id_service : other_item.id_variant) === item.reference}>
                                                    <Dropdown.Item bsPrefix="select2-results__option__text d-flex align-items-center px-2 py-1" onClick={() => executeSelectReferenceRequirement(index, other_item)}>
                                                      { other_item.photos.length > 0
                                                        ?
                                                        <Carousel slide={false} controls={false} indicators={false} touch={false}>
                                                          { other_item.photos.map (( (sub_item, sub_index) => { return (
                                                            <Carousel.Item key={sub_index}>
                                                              <img src={other_item.type === "product" ? imageProduct(sub_item) : imageService(sub_item)} className="d-block w-30px h-30px" alt={lang.labels.photography + sub_index} />
                                                            </Carousel.Item>
                                                          )}))}
                                                        </Carousel>
                                                        :
                                                        <span className="symbol symbol-30px">
                                                          <span className="symbol-label">
                                                            <img src={item.type === "product" ? imgProductDefault : imgServiceDefault} alt={lang.labels.photography} className="w-100 h-100" />
                                                          </span>
                                                        </span>
                                                      }
                                                      <span className="ms-2">{formatContentDetail(other_item)}</span>
                                                      <div className="fw-bolder fs-9 ms-2">{"["}{inventoryTypeValue(lang, other_item.type)}{"]"}</div>
                                                    </Dropdown.Item>
                                                  </li>
                                                )}))}
                                              </>
                                              :
                                              <li className="select2-results__option select2-results__message">
                                                {lang.labels.noRecordsFound}
                                              </li>
                                            }
                                          </ul>
                                        </span>
                                      </span>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                  <AppLegend attribute={{check: requirements.valid[index].reference, name: "requirement-reference", index: index, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                                </td>
                                <td valign="top">
                                  <span className="form-control form-control border-transparent text-center">{item.unit ? item.unit.name : "\u00A0"}</span>
                                </td>
                                <td valign="top">
                                  <div className="bg-light position-relative d-flex align-items-center min-w-150px">
                                    <button className="btn btn-light-primary btn-icon" type="button"  onClick={() => executeSelectQuantityRequirement(index, 'dash')}>
                                      <i className="bi bi-dash fs-1 fw-bolder mx-2" />
                                    </button>
                                    <input className="form-control rounded-0 text-center px-2" type="text" autoComplete="off" name="quantity" value={item.quantity} onChange={(event) => handleChangeQuantityRequirement(index, event)} onKeyUp={() => validateQuantityRequirement(index)} onBlur={() => validateQuantityRequirement(index)} />
                                    <button className="btn btn-light-primary btn-icon" type="button"  onClick={() => executeSelectQuantityRequirement(index, 'plus')}>
                                      <i className="bi bi-plus fs-1 fw-bolder mx-2" />
                                    </button>
                                  </div>
                                  <AppLegend attribute={{check: requirements.valid[index].quantity, name: "requirement-quantity", index: index, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                                </td>
                                <td valign="top">
                                  <span className="form-control form-control border-transparent text-center">{item.price_unit ? formatMoney(Number(item.price_unit)) : "\u00A0"}</span>
                                </td>
                                <td valign="top">
                                  <Dropdown>
                                    <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0" disabled={!requirements.valid[index].reference}>
                                      <span className="selection">
                                        <span className="select2-selection select2-selection--single form-select bg-light px-2" style={{backgroundImage: requirements.valid[index].tax ? "none" : ""}}>
                                          <span className="select2-selection__rendered d-flex align-items-center" role="textbox">
                                            { requirements.valid[index].tax
                                              ?
                                              <>
                                                {formatTaxRate(item.tax_rate)}
                                                <div className="fw-bolder fs-9 ms-2">
                                                  {"["}{item.tax_code}{"]"}
                                                </div>
                                              </>
                                              :
                                              <>
                                                {"\u00A0"}
                                              </>
                                            }
                                          </span>
                                        </span>
                                      </span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                                      <span className="select2-dropdown select2-dropdown--below">
                                        <span className="select2-results">
                                          <ul className="select2-results__options" role="listbox">
                                            <li className={`select2-results__option select2-results__option--selectable ${(settingsForCompany?.tax_fees ?? []).length > 0 && "d-none"}`}>
                                              <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                                            </li>
                                            { (settingsForCompany?.tax_fees ?? []).filter(sub_item => (settingsForCompany?.subject_tax ? sub_item.code !== 'NO_VAT' : sub_item.behavior === 'SALE_ZERO_VAT') && taxSaleCodes().includes(sub_item.code) && sub_item.active && (!sub_item.effectiveFrom || !sub_item.effectiveTo || isWithinInterval(new Date(), {start: parseISO(sub_item.effectiveFrom), end: parseISO(sub_item.effectiveTo)}))).map (( (sub_item, sub_index) => { return (
                                              <li key={sub_index} className={`select2-results__option select2-results__option--selectable ${sub_item.code === item.tax_code && "select2-results__option--selected"}`} role="option" aria-selected={sub_item.code === item.tax_code}>
                                                <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxRateRequirement(index, sub_item)}>{sub_item.label[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</Dropdown.Item>
                                              </li>
                                            )}))}
                                          </ul>
                                        </span>
                                      </span>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                  <AppLegend attribute={{check: requirements.valid[index].tax, name: "requirement-tax", index: index, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                                </td>
                                <td valign="top">
                                  <input className="form-control" type="text" autoComplete="off" name="rebate" disabled={!requirements.valid[index].reference} value={item.rebate_amount} onChange={(event) => handleChangeRebateRequirement(index, event)} onKeyUp={() => validateRebateRequirement(index)} onBlur={() => validateRebateRequirement(index)} />
                                  <AppLegend attribute={{check: requirements.valid[index].rebate, name: "requirement-rebate", index: index, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                                </td>
                                <td valign="top">
                                  <div className="d-flex justify-content-end">
                                    <label className="badge badge-lg badge-light-primary d-flex align-items-center justify-content-end text-nowrap w-100 ps-0 pe-2">
                                      {companyForUser.currency?.code} {formatMoney(calculatorAmount().requirement[index])}
                                    </label>
                                    <button className="btn btn-icon btn-flex btn-light-primary ms-2" type="button" onClick={() => executeRemoveRequirement(index)}>
                                      <span className="svg-icon svg-icon-3 p-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                          <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                          <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                          <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                        </svg>
                                      </span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}))}
                          </>
                          :
                          <tr>
                            <td colSpan={7}>
                              <div className="form-control form-control-sm text-center text-uppercase">{lang.labels.noRecords}</div>
                            </td>
                          </tr>
                        }
                        <tr>
                          <td colSpan={7}>
                            <div id="legend-requirement-required" className="fv-plugins-message-container invalid-feedback d-none">
                              <div data-field="contents" data-validator="notEmpty">{lang.labels.requirementsIsRequired}</div>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={7}>
                            <div className="d-flex align-items-center justify-content-end">
                              <button className="btn btn-light-primary" type="button" onClick={executeAddRequirement}>
                                <div className="d-flex align-items-center">
                                  <span className="svg-icon svg-icon-3 ms-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                                      <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                                      <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                                    </svg>
                                  </span>
                                  {lang.labels.addContent}
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr className="border-top">
                          <td colSpan={7}>
                            <div className="d-flex justify-content-end">
                              <div className="mw-500px">
                                <div className="border-bottom mt-3">
                                  <div className="d-flex align-items-center justify-content-end">
                                    <span className="text-dark text-uppercase fw-bolder">{lang.labels.calculator}</span>
                                    <span className="svg-icon svg-icon-1 ms-3" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.calculatedValuesAreReferential}>
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path opacity="0.3" d="M3 3V17H7V21H15V9H20V3H3Z" fill="black"/>
                                        <path d="M20 22H3C2.4 22 2 21.6 2 21V3C2 2.4 2.4 2 3 2H20C20.6 2 21 2.4 21 3V21C21 21.6 20.6 22 20 22ZM19 4H4V8H19V4ZM6 18H4V20H6V18ZM6 14H4V16H6V14ZM6 10H4V12H6V10ZM10 18H8V20H10V18ZM10 14H8V16H10V14ZM10 10H8V12H10V10ZM14 18H12V20H14V18ZM14 14H12V16H14V14ZM14 10H12V12H14V10ZM19 14H17V20H19V14ZM19 10H17V12H19V10Z" fill="black"/>
                                      </svg>
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex flex-stack px-5 py-2 border-bottom">
                                  <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.subTotal}:</div>
                                  <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().sub_total)}</div>
                                </div>
                                <div className="d-flex flex-stack px-5 py-2 border-bottom">
                                  <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.rebateTotal}:</div>
                                  <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().rebate_total)}</div>
                                </div>
                                <div className="d-flex flex-stack px-5 py-2 border-bottom">
                                  <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.totalAfterRebates}:</div>
                                  <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().after_rebate_total)}</div>
                                </div>
                                <div className="d-flex flex-stack px-5 py-2 border-bottom">
                                  <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.taxTotal}:</div>
                                  <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().tax_total)}</div>
                                </div>
                                <div className="d-flex flex-stack px-5 py-2 border-bottom">
                                  <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.total}:</div>
                                  <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().total)}</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </Container>
              <Container property={(step === 2).toString()}>
                <PosPaymentForm
                  companyId={companyForUser.id}
                  companyCurrency={companyForUser.currency}
                  totalAmount={calculatorAmount().total}
                  customerValue={customer.value}
                  treasuryAccountsForCompany={treasuryAccountsForCompany ?? []}
                  accountingCreditEntries={localAccountingCreditEntries}
                  posCashier={posCashier!}
                  onPaymentsChange={handlePaymentsChange}
                />
              </Container>
            </div>
            <div className="card-footer">
              <div className="row">
                <div className={step === 1 ? "col-md-3 col-lg-4" : "col-md-4 col-lg-3"}>
                  <button className={`btn btn-lg btn-light-primary w-100 ${step === 1 && "d-none"}`} type="button" onClick={executeFormPrevious}>
                    <div className="d-flex align-items-center justify-content-center">
                      <span className="svg-icon svg-icon-3 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.5" x="6" y="11" width="13" height="2" rx="1" fill="black" />
                          <path d="M8.56569 11.4343L12.75 7.25C13.1642 6.83579 13.1642 6.16421 12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75L5.70711 11.2929C5.31658 11.6834 5.31658 12.3166 5.70711 12.7071L11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25C13.1642 17.8358 13.1642 17.1642 12.75 16.75L8.56569 12.5657C8.25327 12.2533 8.25327 11.7467 8.56569 11.4343Z" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.back}
                    </div>
                  </button>
                </div>
                <div className={step === 1 ? "col-md-3" : "d-none"}></div>
                <div className={step === 1 ? "col-md-6 col-lg-5" : "col-md-8 col-lg-9"}>
                  <button className={`btn btn-lg btn-primary w-100 ${step === 2 && "d-none"}`} type="button" onClick={executeFormNext}>
                    <div className="d-flex align-items-center justify-content-center">
                      {lang.labels.continue}
                      <span className="svg-icon svg-icon-3 me-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.5" x="18" y="13" width="13" height="2" rx="1" transform="rotate(-180 18 13)" fill="black" />
                          <path d="M15.4343 12.5657L11.25 16.75C10.8358 17.1642 10.8358 17.8358 11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25L18.2929 12.7071C18.6834 12.3166 18.6834 11.6834 18.2929 11.2929L12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75C10.8358 6.16421 10.8358 6.83579 11.25 7.25L15.4343 11.4343C15.7467 11.7467 15.7467 12.2533 15.4343 12.5657Z" fill="black" />
                        </svg>
                      </span>
                    </div>
                  </button>
                  <button className={`btn btn-lg btn-primary w-100 ${step === 1 && "d-none"}`} type="button" data-kt-indicator={loadIndicator} onClick={submitPosSaleCreate}>
                    <span className="indicator-label">
                      <div className="d-flex align-items-center justify-content-center">
                        {lang.labels.create}
                        <span className="svg-icon svg-icon-3 me-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 4L18 12L10 20H14L21.3 12.7C21.7 12.3 21.7 11.7 21.3 11.3L14 4H10Z" fill="black"/>
                            <path opacity="0.3" d="M3 4L11 12L3 20H7L14.3 12.7C14.7 12.3 14.7 11.7 14.3 11.3L7 4H3Z" fill="black"/>
                          </svg>
                        </span>
                      </div>
                    </span>
                    <span className="indicator-progress">
                      {lang.labels.pleaseWait}
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default POSCreatePage;
